import type { FC } from 'react';

import { useEffect, useRef } from 'react';

import {
  EXPONEA_PRODUCT_INTEREST_DATA,
  EXPONEA_USER_SEGMENT,
  IS_UTM_CPX_USER_SEGMENT,
} from '~/utilities/constants/localStorageKeys';
import { useConsentContext } from '~/utilities/context/dynamic/ConsentContext';
import type { Configuration } from '~/utilities/graphql/codegen';

/**
 * hard timout for 30 seconds to not run the interval indefinitely
 */
const hardTimeout = 30_000;
const poolingInterval = 100;

/**
 * Sync Exponea Rule-Based Personalization from `window.productInterestData` to `sessionStorage`
 *
 * Sync Exponea User Segment from `window.bre` to `sessionStorage`
 *
 * Read data from exponea and write it to `sessionStorage` without using it in the current PLP
 *
 * this data is available pretty late since PLP loads(4-5 seconds), so we cache it in sessionStorage to be used on the next PLP render
 */
export const ExponeaPersonalizationSync: FC<{ configuration: Configuration }> = ({
  configuration: { enableExponea },
}) => {
  const { hasMarketingConsent } = useConsentContext();
  const productInterestElapsedTimeRef = useRef(0);
  const userSegmentElapsedTimeRef = useRef(0);

  const canUseSegmentation = enableExponea && hasMarketingConsent;

  useEffect(() => {
    const intervalID = window.setInterval(() => {
      if (window.productInterestData) {
        sessionStorage?.setItem(
          EXPONEA_PRODUCT_INTEREST_DATA,
          JSON.stringify(window.productInterestData)
        );
        window.clearInterval(intervalID);
      }

      if (productInterestElapsedTimeRef.current >= hardTimeout) {
        window.clearInterval(intervalID);
      }

      productInterestElapsedTimeRef.current += poolingInterval;
    }, poolingInterval);

    return () => window.clearInterval(intervalID);
  }, []);

  useEffect(() => {
    if (!canUseSegmentation) {
      return () => {};
    }

    const intervalID = window.setInterval(() => {
      /**
       * check window.bre set by exponea,
       * it might be an empty object that means the settings are empty
       */
      const breExponeaObject = typeof window.bre === 'object';

      if (sessionStorage?.getItem(IS_UTM_CPX_USER_SEGMENT) === 'true') {
        window.clearInterval(intervalID);

        return;
      }

      if (breExponeaObject) {
        const exponeaUserSegment = window.bre?.segments?.user_segments?.userSegment;

        if (exponeaUserSegment) {
          sessionStorage?.setItem(EXPONEA_USER_SEGMENT, exponeaUserSegment);
          window.clearInterval(intervalID);
        }
      }

      if (userSegmentElapsedTimeRef.current >= hardTimeout) {
        window.clearInterval(intervalID);
      }

      userSegmentElapsedTimeRef.current += poolingInterval;
    }, poolingInterval);

    return () => window.clearInterval(intervalID);
  }, [canUseSegmentation]);

  return null;
};
